import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect } from "react";

import {
	$documentLoaderEffect,
	$registerDocument,
	$registeredDocumentIds,
	$unregisterDocument,
} from "../../internal/document-loader";

/**
 * Hook to access the document registry actions.
 *
 * Use this to register/unregister documents you're interested in loading.
 *
 * @example
 * ```tsx
 * function MyComponent({ documentId }) {
 *   const { registerDocument, unregisterDocument } = useDocumentRegistry();
 *
 *   useEffect(() => {
 *     registerDocument(documentId);
 *     return () => unregisterDocument(documentId);
 *   }, [documentId]);
 * }
 * ```
 */
export function useDocumentRegistry() {
	const setRegister = useSetAtom($registerDocument);
	const setUnregister = useSetAtom($unregisterDocument);
	const registeredIds = useAtomValue($registeredDocumentIds);

	const registerDocument = useCallback(
		(documentId: string) => {
			setRegister(documentId);
		},
		[setRegister]
	);

	const unregisterDocument = useCallback(
		(documentId: string) => {
			setUnregister(documentId);
		},
		[setUnregister]
	);

	return {
		registerDocument,
		unregisterDocument,
		registeredDocumentIds: registeredIds,
	};
}

/**
 * Hook that mounts the document loader effect.
 *
 * Call this once at the app level to activate the loader.
 * The effect will automatically watch registered documents and load their pages.
 *
 * @example
 * ```tsx
 * function App() {
 *   useDocumentLoaderEffect();
 *   return <Router />;
 * }
 * ```
 */
export function useDocumentLoaderEffect() {
	// Reading the atomEffect mounts it
	useAtom($documentLoaderEffect);
}

/**
 * Hook that automatically loads the current document based on the URL location.
 *
 * Call this in components that need document data to be loaded.
 * The loader watches `$location` and automatically loads metadata
 * and editor states for the current document.
 *
 * @example
 * ```tsx
 * function DocumentPage() {
 *   useDocumentAutoLoader();
 *   const { editorState } = useEditorState();
 *   // editorState will be populated once loading completes
 * }
 * ```
 */
export function useDocumentAutoLoader() {
	// Subscribing to the effect activates location-based document loading
	useAtom($documentLoaderEffect);
}
